import { extendTheme } from "@chakra-ui/react";
import { OryAuthService } from "./OryAuthService";

export const localDateFormat = "yyyy-MM-DD";

export const CONTENT_WIDTH = "1280px";
export const DATES_COLOR = "rgba(128, 90, 213, 0.06)";
export const FINANCIAL_COLOR = "rgba(57, 161, 105, 0.06)";
export const COMMERCIAL_COLOR = "rgba(49, 130, 206, 0.06)";
export const TERMS_AND_CONDITIONS_COLOR = "rgba(229, 62, 63, 0.06)";
export const OTHER_COLOR = "rgba(49, 151, 149, 0.06)";
export const OPTIONS_COLOR = "rgba(0, 0, 149, 0.06)";
export const RICH_TEXT_TEXT_COLORS = {
  black: "#1A202C",
  grey: "#4A5568",
  red: "#C53030",
  orange: "#C05621",
  green: "#2F855A",
  blue: "#2B6CB0",
  purple: "#6B46C1",
} as const;
export const RICH_TEXT_HIGHLIGHT_COLORS = {
  yellow: "#FEFCBF",
  green: "#C6F6D5",
  blue: "#BEE3F8",
  pink: "#FED7E2",
  grey: "#CBD5E0",
} as const;
export const CHART_TEXT_COLOR = { light: "rgba(0, 0, 0, 0.8)", dark: "rgba(255, 255, 255, 0.8)" };
export const CHART_GRID_COLOR = { light: "rgba(0, 0, 0, 0.1)", dark: "rgba(255, 255, 255, 0.1)" };
export const FINANCIALS_BAR_COLORS = [
  "rgba(75, 192, 192, 0.6)",
  "rgba(255, 99, 132, 0.6)",
  "rgba(54, 162, 235, 0.6)",
  "rgba(255, 206, 86, 0.6)",
];
export const ASSESSMENT_RADAR_FILL_COLOR = "rgba(75, 192, 192, 0.2)";
export const ASSESSMENT_RADAR_LINE_COLOR = "rgba(75, 192, 192, 1)";
export const ASSESSMENT_RADAR_POINT_ACCENT_COLOR = "#fff";

export const authService = new OryAuthService();

type StringVarName =
  | "VITE_WEBAPP_BASE_URL"
  | "VITE_BFF_BASE_URL"
  | "VITE_ORY_BASE_URL"
  | "VITE_S3_BASE_URL"
  | "VITE_PUSHER_CLUSTER"
  | "VITE_PUSHER_KEY"
  | "VITE_DATADOG_APPLICATION_ID"
  | "VITE_DATADOG_CLIENT_TOKEN"
  | "VITE_DATADOG_ENV"
  | "VITE_INTERCOM_APP_ID"
  | "VITE_INTERCOM_BASE_URL"
  | "VITE_FENCY_PUBLISHABLE_KEY"
  | "VITE_DOFFIN_NOTICE_BASE_URL";

export const requireStringEnvVar = (varName: StringVarName): string => {
  switch (varName) {
    case "VITE_BFF_BASE_URL":
      return import.meta.env.VITE_BFF_BASE_URL;
    case "VITE_WEBAPP_BASE_URL":
      return import.meta.env.VITE_WEBAPP_BASE_URL;
    case "VITE_ORY_BASE_URL":
      return import.meta.env.VITE_ORY_BASE_URL;
    case "VITE_S3_BASE_URL":
      return import.meta.env.VITE_S3_BASE_URL;
    case "VITE_PUSHER_CLUSTER":
      return import.meta.env.VITE_PUSHER_CLUSTER;
    case "VITE_PUSHER_KEY":
      return import.meta.env.VITE_PUSHER_KEY;
    case "VITE_DATADOG_APPLICATION_ID":
      return import.meta.env.VITE_DATADOG_APPLICATION_ID;
    case "VITE_DATADOG_CLIENT_TOKEN":
      return import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
    case "VITE_DATADOG_ENV":
      return import.meta.env.VITE_DATADOG_ENV;
    case "VITE_INTERCOM_APP_ID":
      return import.meta.env.VITE_INTERCOM_APP_ID;
    case "VITE_INTERCOM_BASE_URL":
      return import.meta.env.VITE_INTERCOM_BASE_URL;
    case "VITE_FENCY_PUBLISHABLE_KEY":
      return import.meta.env.VITE_FENCY_PUBLISHABLE_KEY;
    case "VITE_DOFFIN_NOTICE_BASE_URL":
      return import.meta.env.VITE_DOFFIN_NOTICE_BASE_URL;
  }
};

type BooleanVarName =
  | "VITE_ENABLE_DUMMY_COMPANY_PAGE"
  | "VITE_ENABLE_ADVANCED_EVALUATION"
  | "VITE_ENABLE_CONTRACTS_TABLE"
  | "VITE_ENABLE_DEV_TOOLS"
  | "VITE_ENABLE_FOCUS_MODE";

export const requireBooleanEnvVar = (varName: BooleanVarName): boolean => {
  switch (varName) {
    case "VITE_ENABLE_DUMMY_COMPANY_PAGE":
      return import.meta.env.VITE_ENABLE_DUMMY_COMPANY_PAGE === "true";
    case "VITE_ENABLE_ADVANCED_EVALUATION":
      return import.meta.env.VITE_ENABLE_ADVANCED_EVALUATION === "true";
    case "VITE_ENABLE_CONTRACTS_TABLE":
      return import.meta.env.VITE_ENABLE_CONTRACTS_TABLE === "true";
    case "VITE_ENABLE_DEV_TOOLS":
      return import.meta.env.VITE_ENABLE_DEV_TOOLS === "true";
    case "VITE_ENABLE_FOCUS_MODE":
      return import.meta.env.VITE_ENABLE_FOCUS_MODE === "true";
  }
};

export type Locale = "no" | "en";

const config = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

const outlineFieldBorder = {
  borderColor: "smInputBorder",
  _hover: { borderColor: "smInputBorderHover" },
};

const tealSolidButton = ({ colorScheme }: { colorScheme: string }) =>
  colorScheme === "teal" ? { _hover: { bg: "smPrimaryHover" }, _active: { bg: "smPrimaryHover" } } : {};

export const theme = extendTheme({
  config,
  colors: {
    teal: {
      500: "#0F6C68",
      600: "#0F6C68",
    },
  },
  semanticTokens: {
    colors: {
      smText: {
        default: "black",
        _dark: "white",
      },
      smPrimary: {
        default: "teal.500",
        _dark: "teal.200",
      },
      smPrimaryHover: {
        default: "#0A4C49",
        _dark: "teal.300",
      },
      smPrimarySoft: {
        default: "teal.400",
        _dark: "teal.300",
      },
      smPrimarySofter: {
        default: "teal.50",
        _dark: "teal.900",
      },
      smPrimarySofterBorder: {
        default: "teal.200",
        _dark: "teal.700",
      },
      smPrimarySolid: {
        default: "teal.500",
        _dark: "teal.600",
      },
      smSecondary: {
        default: "blue.400",
        _dark: "blue.100",
      },
      smSecondarySoft: {
        default: "blue.50",
        _dark: "blue.800",
      },
      smTertiary: {
        default: "purple.500",
        _dark: "purple.200",
      },
      smTertiarySoft: {
        default: "purple.100",
        _dark: "purple.900",
      },
      smQuaternary: {
        default: "orange.500",
        _dark: "orange.200",
      },
      smQuaternarySoft: {
        default: "orange.50",
        _dark: "orange.900",
      },
      smBackground: {
        default: "white",
        _dark: "gray.800",
      },
      smBackgroundSecondary: {
        default: "gray.100",
        _dark: "gray.900",
      },
      smBackgroundTertiary: {
        default: "gray.300",
        _dark: "gray.700",
      },
      smModalBackground: {
        default: "white",
        _dark: "gray.900",
      },
      smBorder: {
        default: "gray.200",
        _dark: "gray.600",
      },
      // The line between rows inside a container that already has a frame — Chakra's own table uses
      // this weight, and anything drawn beside a table has to match it or read as heavier.
      smBorderSubtle: {
        default: "gray.100",
        _dark: "gray.700",
      },
      smInputBorder: {
        default: "#798793",
        _dark: "gray.500",
      },
      smInputBorderHover: {
        default: "#566069",
        _dark: "gray.400",
      },
      smTextUpcoming: {
        default: "#566069",
        _dark: "gray.400",
      },
      smTextDisabled: {
        default: "gray.400",
        _dark: "gray.600",
      },
      smMuted: {
        default: "gray.600",
        _dark: "gray.400",
      },
      smTealShadow: {
        default: "teal.100",
        _dark: "teal.800",
      },
      smBlueShadow: {
        default: "blue.100",
        _dark: "blue.800",
      },
      smPinnedColumnEdge: {
        default: "gray.200",
        _dark: "gray.500",
      },
      smScrollEdgeShadow: {
        default: "blackAlpha.300",
        _dark: "whiteAlpha.600",
      },
      smDanger: {
        default: "red.300",
        _dark: "red.900",
      },
      smSuccess: {
        default: "green.500",
        _dark: "green.300",
      },
      smError: {
        default: "red.500",
        _dark: "red.300",
      },
      // Design profile v0.3 error colour.
      smAlertBadge: "#C4432F",
      smWarning: {
        default: "orange.500",
        _dark: "orange.300",
      },
      // Chart identity colors, one per slice of the home page breakdown charts. The phase set is
      // validated as a categorical palette (CVD separation, chroma, lightness, contrast) against
      // both light and dark surfaces — re-validate before swapping steps. The expiry set predates
      // that check and keeps its established look.
      smChartExpiryMonth: "red.400",
      smChartExpiryQuarter: "orange.400",
      smChartExpiryHalfYear: "yellow.400",
      smChartExpiryYear: "teal.400",
      smChartPhaseDraft: "yellow.600",
      smChartPhaseAnnounced: "blue.500",
      smChartPhaseEvaluation: "orange.500",
      smChartPhaseAward: "purple.500",
      smChartPhaseCompleted: "green.500",
      smChartPhaseCancelled: "gray.500",
      smChartSeries1: "blue.500",
      smChartSeries2: "orange.500",
      smChartSeries3: "purple.500",
      smChartSeries4: "green.500",
      smChartSeries5: "yellow.600",
      smChartSeries6: "gray.500",
      smSuccessSoft: {
        default: "green.50",
        _dark: "green.900",
      },
      smSuccessSoftBorder: {
        default: "green.200",
        _dark: "green.700",
      },
      smWarningSoft: {
        default: "orange.50",
        _dark: "orange.900",
      },
      smWarningSoftBorder: {
        default: "orange.200",
        _dark: "orange.700",
      },
      smStatusOkBg: {
        default: "green.50",
        _dark: "green.900",
      },
      smStatusOkText: {
        default: "green.700",
        _dark: "green.200",
      },
      smStatusOkBorder: {
        default: "green.200",
        _dark: "green.700",
      },
      smStatusOkAccent: {
        default: "green.300",
        _dark: "green.500",
      },
      smStatusIssueBg: {
        default: "orange.50",
        _dark: "orange.900",
      },
      smStatusIssueText: {
        default: "orange.700",
        _dark: "orange.200",
      },
      smStatusIssueBorder: {
        default: "orange.200",
        _dark: "orange.700",
      },
      smStatusIssueAccent: {
        default: "orange.300",
        _dark: "orange.500",
      },
      smStatusNeutralBg: {
        default: "gray.50",
        _dark: "gray.800",
      },
      smStatusNeutralText: {
        default: "gray.700",
        _dark: "gray.200",
      },
      smStatusNeutralBorder: {
        default: "gray.200",
        _dark: "gray.600",
      },
      smStatusNeutralAccent: {
        default: "gray.300",
        _dark: "gray.500",
      },
      smEspdCategoryBg: {
        default: "gray.50",
        _dark: "gray.800",
      },
      smEspdCriterionBg: {
        default: "gray.100",
        _dark: "gray.700",
      },
      smEvalGroupRowBg: {
        default: "gray.50",
        _dark: "gray.700",
      },
      smErrorSoft: {
        default: "red.50",
        _dark: "red.900",
      },
      smErrorSoftBorder: {
        default: "red.400",
        _dark: "red.700",
      },
      smErrorStrong: {
        default: "red.700",
        _dark: "red.200",
      },
      smErrorMuted: {
        default: "red.600",
        _dark: "red.300",
      },
      smProtocolRowAltBg: {
        default: "#f8fafc",
        _dark: "gray.700",
      },
      smInfo: {
        default: "blue.500",
        _dark: "blue.300",
      },
      smChipTealBg: { default: "teal.50", _dark: "teal.900" },
      smChipTealFg: { default: "teal.600", _dark: "teal.200" },
      smChipAmberBg: { default: "orange.50", _dark: "orange.900" },
      smChipAmberFg: { default: "orange.600", _dark: "orange.200" },
      smChipBlueBg: { default: "blue.50", _dark: "blue.900" },
      smChipBlueFg: { default: "blue.600", _dark: "blue.200" },
      smChipGreenBg: { default: "green.50", _dark: "green.900" },
      smChipGreenFg: { default: "green.600", _dark: "green.200" },
      smChipRedBg: { default: "red.50", _dark: "red.900" },
      smChipRedFg: { default: "red.600", _dark: "red.200" },
      smChipVioletBg: { default: "purple.50", _dark: "purple.900" },
      smChipVioletFg: { default: "purple.600", _dark: "purple.200" },
      smContractSectionHeading: { default: "#808B9B", _dark: "#808B9B" },
      smTextSecondary: { default: "#717171", _dark: "#717171" },
      smTaskActivityText: { default: "#42526e", _dark: "#42526e" },
      smPageIntroText: { default: "#585858", _dark: "#585858" },
      smFileNameText: { default: "#565555", _dark: "#565555" },
      smEmptyListText: { default: "#5f6368", _dark: "#5f6368" },
      smLinkItemText: { default: "#4a4848", _dark: "#4a4848" },
      smLinkItemIcon: { default: "#767676", _dark: "#767676" },
      smOrgCardHeading: { default: "#a4a7a6", _dark: "#a4a7a6" },
      smOrgCardText: { default: "#727272", _dark: "#727272" },
      smInputAddonDisabledText: { default: "#c9cfd6", _dark: "#c9cfd6" },
      smPageDivider: { default: "#e1e6ee", _dark: "#e1e6ee" },
      smPanelBackground: { default: "#f6f8fb", _dark: "#f6f8fb" },
      smInvitePageBackground: { default: "#f8f8f8", _dark: "#f8f8f8" },
      smListItemHoverBg: { default: "rgba(49, 151, 149, 0.08)", _dark: "rgba(49, 151, 149, 0.08)" },
      smAnalysisInfoIcon: { default: "#3082ce", _dark: "#3082ce" },
      smAnalysisMissingIcon: { default: "#e53d3d", _dark: "#e53d3d" },
      smAnalysisSuggestionIcon: { default: "#7f5ad5", _dark: "#7f5ad5" },
      smSelectorPlaceholder: { default: "#a0aec0", _dark: "#a0aec0" },
      smSustainabilityCheck: { default: "#38a169", _dark: "#38a169" },
    },
    shadows: {
      inner: {
        default: "inset 0 2px 4px 0 rgba(0,0,0,0.06), inset 0 -1px 2px 0 rgba(0,0,0,0.06)",
        _dark: "inset 0px 2px 4px 0 rgba(255,255,255,0.06), inset 0px -1px 2px 0 rgba(255,255,255,0.06)",
      },
      smFocusRing: {
        default: "0 0 0 3px rgba(9, 135, 125, 0.28)",
        _dark: "0 0 0 3px rgba(79, 209, 197, 0.32)",
      },
      // Chakra's own focus token, which every component's _focusVisible points at — Button,
      // IconButton, CloseButton, Tab, Checkbox and the rest. Left at its default it is a blue halo
      // that belongs to no palette in this app; pointed here it is the brand ring everywhere at once.
      outline: "var(--chakra-shadows-smFocusRing)",
      smCard: "0 1px 15px rgba(71, 85, 105, 0.16)",
      smCardRaised: "0 3px 15px rgba(71, 85, 105, 0.16)",
      smAuthCard: "0 9px 46px 8px rgb(0,0,0,0.2)",
      smModalFooterLift: "0 -8px 24px rgba(15, 23, 42, 0.05)",
      smInsetFocusRing: "inset 0 0 0 2px var(--chakra-colors-smPrimary)",
      smFocusBorder: "0 0 0 1px var(--chakra-colors-smPrimary)",
    },
  },
  components: {
    Switch: {
      defaultProps: { colorScheme: "teal" },
    },
    Button: {
      variants: { solid: tealSolidButton },
    },
    Input: {
      variants: { outline: { field: outlineFieldBorder } },
    },
    NumberInput: {
      variants: { outline: { field: outlineFieldBorder } },
    },
    Select: {
      variants: { outline: { field: outlineFieldBorder } },
    },
    Textarea: {
      variants: { outline: outlineFieldBorder },
    },
  },
});

export const bundleCommit: string | undefined = import.meta.env.VITE_COMMIT_SHA || undefined;
